import React from "react";
import { Dimensions, StyleSheet, ImageSourcePropType } from "react-native";
import Animated, {
  Extrapolation,
  interpolate,
  SharedValue,
  useAnimatedStyle,
} from "react-native-reanimated";
import { LinearGradient } from "expo-linear-gradient";
import MaskedView from "@react-native-masked-view/masked-view";
import LBox from "@components/LBox";
import { getViewPort } from "@utils/web/config";

export const { width } = getViewPort(Dimensions.get("window"));
export const _slideWidth = width * 0.82;
export const _slideHeight = width / 5;
export const _spacing = 18;
export const _topSpacing = 30;

const scaleFactor = 0.2;
const _translateX = _slideWidth * scaleFactor * 2;

type SlideProps = {
  photo: ImageSourcePropType;
  index: number;
  scrollX: SharedValue<number>;
  children?: React.ReactNode;
};

type BackdropProps = {
  photo: ImageSourcePropType;
  index: number;
  scrollX: SharedValue<number>;
};

export function Slide({ photo, index, scrollX, children }: SlideProps) {
  const containerStylez = useAnimatedStyle(() => {
    return {
      transform: [
        {
          scale: interpolate(
            scrollX.value,
            [index - 1, index, index + 1],
            [0.8, 1, 0.8],
            Extrapolation.CLAMP,
          ),
        },
      ],
    };
  });

  const stylez = useAnimatedStyle(() => {
    return {
      transform: [
        {
          scale: 1 + scaleFactor,
        },
        {
          translateX: interpolate(
            scrollX.value,
            [index - 1, index, index + 1],
            [-_translateX, 0, _translateX],
            Extrapolation.CLAMP,
          ),
        },
      ],
    };
  });

  return (
    <Animated.View
      style={[
        {
          borderRadius: 12,
          overflow: "hidden",
        },
        containerStylez,
      ]}
    >
      <Animated.View
        style={{
          borderRadius: 12,
          overflow: "hidden",
        }}
      >
        <Animated.Image
          source={photo}
          style={[
            {
              width: _slideWidth,
              height: _slideHeight,
              objectFit: "cover",
            },
            stylez,
          ]}
        />
      </Animated.View>
      <LBox style={StyleSheet.absoluteFill}>{children}</LBox>
    </Animated.View>
  );
}

export function Backdrop({ photo, index, scrollX }: BackdropProps) {
  const stylez = useAnimatedStyle(() => {
    return {
      opacity: interpolate(
        scrollX.value,
        [index - 1, index, index + 1],
        [0, 1, 0],
        Extrapolation.CLAMP,
      ),
    };
  });

  return (
    <Animated.View style={[StyleSheet.absoluteFill, stylez]}>
      <MaskedView
        style={[StyleSheet.absoluteFill, { transform: [{ translateY: 40 }] }]}
        maskElement={
          <LinearGradient
            colors={["transparent", "black"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            locations={[0, 0.15]}
            style={StyleSheet.absoluteFill}
          />
        }
      >
        <LBox style={StyleSheet.absoluteFill}>
          <Animated.Image
            source={photo}
            style={{ transform: [{ scale: 2 }] }}
          />
        </LBox>
      </MaskedView>
    </Animated.View>
  );
}

export function Indicators({
  numberOfCards,
  scrollX,
}: {
  numberOfCards: number;
  scrollX: SharedValue<number>;
}) {
  return (
    <LBox flexDirection="row" gap="s1">
      {Array.from({ length: numberOfCards }).map((_, index) => (
        <Indicator key={index} index={index} scrollX={scrollX} />
      ))}
    </LBox>
  );
}

export const Indicator = ({
  index,
  scrollX,
}: {
  index: number;
  scrollX: SharedValue<number>;
}) => {
  const stylez = useAnimatedStyle(() => {
    return {
      width: interpolate(
        scrollX.value,
        [index - 1, index, index + 1],
        [4, 8, 4],
        Extrapolation.CLAMP,
      ),
      opacity: interpolate(
        scrollX.value,
        [index - 1, index, index + 1],
        [0.5, 1, 0.5],
        Extrapolation.CLAMP,
      ),
    };
  });

  return (
    <LBox
      key={index}
      width={4}
      height={4}
      borderRadius="m-l"
      bg="white"
      style={stylez}
    />
  );
};
